import { Check, ChevronDown, ChevronLeft, ChevronRight, X } from 'lucide-react';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { PlaceholderMemberService, formatCurrency } from '@splitfiesta/core';
import { useTranslation } from '@splitfiesta/i18n';
import type {
  CreateSettlementDto,
  GroupWithDashboard,
} from '@splitfiesta/shared';
import { PAYMENT_METHODS, getCurrencyDecimals } from '@splitfiesta/shared';
import { Avatar, Button, Card } from '@splitfiesta/ui';

import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { PlaceholderBadge } from '@/components/members/PlaceholderBadge';
import CurrencyInput from '@/components/ui/CurrencyInput';
import { Portal } from '@/components/ui/Portal';

import { useDebtSuggestionsQuery } from '@/hooks/queries/useBalances';
import { useCreateSettlementMutation } from '@/hooks/queries/useSettlements';
import { useToast } from '@/hooks/useToast';

import { resolveUserName } from '@/lib/displayName';

import { useAuthStore } from '@/stores/authStore';

import { celebrate } from '@/utils/confetti';

interface DebtRecommendation {
  fromUser: {
    id: string;
    name: string;
    profileImage?: string;
  };
  toUser: {
    id: string;
    name: string;
    profileImage?: string;
  };
  amount: number;
  currency: string;
  priority?: number;
}

interface CreateSettlementModalProps {
  isOpen: boolean;
  onClose: () => void;
  group: GroupWithDashboard;
  preselectedCreditorId?: string;
}

type Step = 1 | 2 | 3;

export function CreateSettlementModal({
  isOpen,
  onClose,
  group,
  preselectedCreditorId,
}: CreateSettlementModalProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const user = useAuthStore(state => state.user);

  const [currentStep, setCurrentStep] = useState<Step>(1);
  const [formData, setFormData] = useState<Partial<CreateSettlementDto>>({
    groupId: group.id,
    currency: group.currency,
    method: 'cash',
  });

  const [amountString, setAmountString] = useState<string | undefined>('');

  const [errors, setErrors] = useState<
    Partial<Record<keyof CreateSettlementDto, string>>
  >({});

  const createSettlementMutation = useCreateSettlementMutation();

  // Fetch simplified debt suggestions to get accurate bilateral amounts
  // Pass filterByUser=true to only get debts where current user owes money
  const {
    data: debtSuggestions,
    isLoading: isLoadingDebts,
    refetch,
  } = useDebtSuggestionsQuery(
    group.id,
    isOpen,
    true // filterByUser - only show debts where current user owes money
  );

  // Refetch data every time the modal opens to get fresh pending settlements
  useEffect(() => {
    // Only refetch if modal is open
    if (!isOpen) return;

    // Refetch in next tick to avoid setting state during render
    const timer = setTimeout(() => {
      refetch();
    }, 0);

    return () => clearTimeout(timer);
  }, [isOpen, refetch]);

  // Get recommendations and pending settlements from the API response
  const availableDebts = useMemo(() => {
    return (
      (debtSuggestions && 'recommendations' in debtSuggestions
        ? debtSuggestions.recommendations
        : []) || []
    );
  }, [debtSuggestions]);

  // Get pending settlements that involve the current user
  const userPendingSettlements = useMemo(() => {
    if (
      !user?.id ||
      !debtSuggestions ||
      !('pendingSettlements' in debtSuggestions)
    ) {
      return [];
    }

    // Filter pending settlements where current user is the payer (fromUser)
    return debtSuggestions.pendingSettlements.filter(
      settlement => settlement.fromUser.id === user.id
    );
  }, [debtSuggestions, user]);

  // The simplified-debt recommendations carry no placeholder flag, so the
  // group balances stay the source of truth for it in both branches below.
  const placeholderIds = useMemo(
    () => PlaceholderMemberService.placeholderIdSet(group.balances ?? []),
    [group.balances]
  );

  // Filter members that the current user owes money to
  const membersUserOwes = useMemo(() => {
    if (!user?.id) return [];

    // If we have debt suggestions from the API (simplified debts response),
    // ALWAYS use the recommendations array as it accounts for pending settlements
    if (debtSuggestions && 'recommendations' in debtSuggestions) {
      // Use the debt recommendations from API (already adjusted for pending settlements)
      return availableDebts
        .filter(debt => debt.fromUser.id === user.id)
        .map(debt => ({
          userId: debt.toUser.id,
          userName: debt.toUser.name,
          profileImage: debt.toUser.profileImage,
          balance: 0, // Not used in this context
          currency: debt.currency,
          isPlaceholder: placeholderIds.has(debt.toUser.id),
        }));
    } else {
      // Fallback for non-simplified groups or when API data is not available
      const currentUserBalance =
        group.balances.find(b => b.userId === user.id)?.balance || 0;

      if (currentUserBalance >= 0) {
        // User doesn't owe money to anyone
        return [];
      }

      // User owes money, show all members with positive balances
      return group.balances
        .filter(member => member.userId !== user.id && member.balance > 0)
        .map(member => ({
          userId: member.userId,
          userName: member.userName,
          profileImage: member.profileImage,
          balance: member.balance,
          currency: member.currency,
          isPlaceholder: Boolean(member.isPlaceholder),
        }));
    }
  }, [group.balances, debtSuggestions, availableDebts, user, placeholderIds]);

  // Create a map of debt amounts for easy lookup
  const debtMap = useMemo(
    () =>
      new Map<string, DebtRecommendation>(
        availableDebts.map(debt => [debt.toUser.id, debt as DebtRecommendation])
      ),
    [availableDebts]
  );

  // Reset form when modal opens
  useEffect(() => {
    if (!isOpen) return;

    // Reset form in next tick to avoid state updates during render
    const resetTimer = setTimeout(() => {
      setCurrentStep(1);
      setFormData({
        groupId: group.id,
        currency: group.currency,
        method: 'cash',
      });
      setAmountString('');
      setErrors({});

      // Celebrate only if user doesn't owe anyone AND has no pending settlements
      if (membersUserOwes.length === 0 && userPendingSettlements.length === 0) {
        const celebrateTimer = setTimeout(() => {
          celebrate('subtle');
        }, 400);
        return () => clearTimeout(celebrateTimer);
      }
    }, 0);

    return () => clearTimeout(resetTimer);
  }, [
    isOpen,
    group.id,
    group.currency,
    membersUserOwes.length,
    userPendingSettlements.length,
  ]);

  // Handle preselected creditor - auto-select and advance to step 2
  useEffect(() => {
    if (!isOpen || !preselectedCreditorId || isLoadingDebts) return;

    // Wait for debts to be loaded before auto-selecting
    const timer = setTimeout(() => {
      const debt = debtMap.get(preselectedCreditorId);
      const newAmount = debt ? debt.amount : undefined;

      setFormData(prev => ({
        ...prev,
        toUserId: preselectedCreditorId,
        amount: newAmount,
      }));

      // Update amount string for CurrencyInput
      if (debt && debt.amount) {
        const decimals = getCurrencyDecimals(group.currency);
        setAmountString(debt.amount.toFixed(decimals));
      }

      // Auto-advance to step 2
      setCurrentStep(2);
    }, 100);

    return () => clearTimeout(timer);
  }, [isOpen, preselectedCreditorId, isLoadingDebts, debtMap, group.currency]);

  const validateStep = useCallback(
    (step: Step): boolean => {
      const newErrors: Partial<Record<keyof CreateSettlementDto, string>> = {};

      if (step === 1) {
        if (!formData.toUserId) {
          newErrors.toUserId = t('settlements:errors.selectReceiver');
        }
      } else if (step === 2) {
        if (!formData.amount || formData.amount <= 0) {
          newErrors.amount = t('settlements:errors.invalidAmount');
        }
      }

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },
    [formData.toUserId, formData.amount, t]
  );

  const handleNext = useCallback(() => {
    if (validateStep(currentStep)) {
      setCurrentStep((currentStep + 1) as Step);
    }
  }, [currentStep, validateStep]);

  const handleBack = useCallback(() => {
    setCurrentStep((currentStep - 1) as Step);
    setErrors({});
  }, [currentStep]);

  const handleSubmit = useCallback(async () => {
    if (!validateStep(2)) {
      return;
    }

    try {
      await createSettlementMutation.mutateAsync(
        formData as CreateSettlementDto
      );

      // Celebrate with confetti when settlement is created
      celebrate('medium');

      toast({
        title: t('settlements:messages.settlementCreated'),
        type: 'success',
      });
      onClose();
    } catch (_error) {
      toast({
        title: t('common:errors.genericError'),
        description: t('settlements:errors.createFailed'),
        type: 'error',
      });
    }
  }, [validateStep, createSettlementMutation, formData, toast, t, onClose]);

  // Handle Enter key for step 2 and 3
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();

        if (currentStep === 2) {
          // Step 2: Click Next if amount is valid
          if (formData.amount && formData.amount > 0) {
            handleNext();
          }
        } else if (currentStep === 3) {
          // Step 3: Click Settle if not already submitting
          if (!createSettlementMutation.isPending) {
            handleSubmit();
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    isOpen,
    currentStep,
    formData.amount,
    createSettlementMutation.isPending,
    handleNext,
    handleSubmit,
  ]);

  const selectedDebt = debtMap.get(formData.toUserId || '');
  const selectedMember = membersUserOwes.find(
    m => m.userId === formData.toUserId
  );

  const handleRecipientSelect = (userId: string) => {
    const debt = debtMap.get(userId);
    const newAmount = debt ? debt.amount : formData.amount;
    setFormData({
      ...formData,
      toUserId: userId,
      // Auto-populate amount with the suggested debt amount if available
      amount: newAmount,
    });
    // Also update the string representation for CurrencyInput
    if (debt && debt.amount) {
      const decimals = getCurrencyDecimals(group.currency);
      setAmountString(debt.amount.toFixed(decimals));
    }
    // Auto-advance to next step
    setTimeout(() => setCurrentStep(2), 300);
  };

  const handleSuggestedAmount = () => {
    if (selectedDebt) {
      const decimals = getCurrencyDecimals(group.currency);
      setAmountString(selectedDebt.amount.toFixed(decimals));
      setFormData({
        ...formData,
        amount: selectedDebt.amount,
      });
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        {/* Backdrop */}
        <div
          className="fixed inset-0 bg-black/50"
          onClick={onClose}
          data-testid="modal-backdrop"
        />

        {/* Modal Content */}
        <Card
          className="relative z-50 w-full max-w-[90vw] sm:w-auto sm:min-w-[500px] sm:max-w-[650px] mx-2 p-0"
          padding="none"
          data-testid="create-settlement-modal"
        >
          <div className="relative overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {t('settlements:createSettlement')}
                </h2>
                {/* Step Indicator */}
                <div className="flex items-center gap-1.5">
                  {[1, 2, 3].map(step => (
                    <div
                      key={step}
                      className={`h-2 w-2 rounded-full transition-all ${
                        step === currentStep
                          ? 'bg-green-600 w-6'
                          : step < currentStep
                            ? 'bg-green-400'
                            : 'bg-gray-300 dark:bg-gray-600'
                      }`}
                    />
                  ))}
                </div>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={onClose}
                aria-label={t('common:actions.close')}
                className="p-2 min-h-[44px] min-w-[44px]"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            {/* Step Content */}
            <div className="px-6 py-6">
              {/* Step 1: Select Recipient */}
              {currentStep === 1 && (
                <div className="space-y-4 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('settlements:labels.whoPayingTo')}
                    </h3>
                    {membersUserOwes.length > 0 && (
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {t('settlements:labels.selectRecipient')}
                      </p>
                    )}
                  </div>

                  {isLoadingDebts ? (
                    /* Show loading state while fetching debt suggestions */
                    <div className="text-center py-12">
                      <LoadingSpinner size="lg" className="mx-auto mb-4" />
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        Loading settlement options...
                      </p>
                    </div>
                  ) : membersUserOwes.length === 0 &&
                    userPendingSettlements.length > 0 ? (
                    /* Show pending settlements - debts covered by pending settlements */
                    <div className="text-center py-8">
                      <div className="text-5xl mb-4">⏳</div>
                      <h4 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                        {t('settlements:labels.pendingSettlements')}
                      </h4>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
                        {t('settlements:labels.pendingSettlementsDescription')}
                      </p>

                      {/* List of pending settlements */}
                      <div className="space-y-3 max-w-md mx-auto">
                        {userPendingSettlements.map(settlement => (
                          <div
                            key={settlement.id}
                            className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg p-4"
                          >
                            <div className="flex items-center justify-between gap-3">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <Avatar
                                  name={settlement.toUser.name}
                                  src={settlement.toUser.profileImage}
                                  size="sm"
                                  className="shrink-0"
                                />
                                <div className="min-w-0 flex-1">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {t('settlements:labels.payingTo')}{' '}
                                    {resolveUserName(settlement.toUser.name, t)}
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    {t(
                                      'settlements:labels.awaitingConfirmation'
                                    )}
                                  </p>
                                </div>
                              </div>
                              <div className="text-right shrink-0">
                                <p className="text-lg font-bold text-orange-600 dark:text-orange-400">
                                  {formatCurrency(
                                    settlement.amount,
                                    settlement.currency
                                  )}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : membersUserOwes.length === 0 ? (
                    /* Show celebration - truly settled up */
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">🎉</div>
                      <p className="text-gray-600 dark:text-gray-400">
                        {t('settlements:placeholders.noDebts')}
                      </p>
                    </div>
                  ) : (
                    /* Show normal settlement creation flow - user still has debts to pay */
                    <div className="space-y-3 max-h-[350px] overflow-y-auto">
                      {membersUserOwes.map(member => {
                        const debt = debtMap.get(member.userId);
                        return (
                          <button
                            key={member.userId}
                            onClick={() => handleRecipientSelect(member.userId)}
                            className="w-full p-4 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-700 transition-all hover:border-green-500 dark:hover:border-green-500 hover:shadow-md active:scale-[0.98]"
                            data-testid={`recipient-card-${member.userId}`}
                          >
                            {/* Desktop Layout: Horizontal */}
                            <div className="hidden sm:flex items-center justify-between gap-4">
                              <div className="flex items-center gap-3 min-w-0 flex-1">
                                <Avatar
                                  name={member.userName}
                                  src={member.profileImage}
                                  size="md"
                                  className="shrink-0"
                                />
                                <p className="font-medium text-gray-900 dark:text-gray-100 whitespace-nowrap">
                                  {resolveUserName(member.userName, t)}
                                </p>
                                {member.isPlaceholder && (
                                  <PlaceholderBadge className="ml-2" />
                                )}
                              </div>
                              {debt && group.simplifyDebts && (
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                  <p className="text-xl font-bold text-green-600 whitespace-nowrap">
                                    {formatCurrency(
                                      debt.amount,
                                      debt.currency || group.currency
                                    )}
                                  </p>
                                  <p className="text-xs text-gray-600 dark:text-gray-400">
                                    {t('settlements:suggestions.youOwe')}
                                  </p>
                                </div>
                              )}
                              <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                            </div>

                            {/* Mobile Layout: Stacked */}
                            <div className="flex sm:hidden flex-col gap-3">
                              <div className="flex items-center justify-end gap-3">
                                <Avatar
                                  name={member.userName}
                                  src={member.profileImage}
                                  size="md"
                                  className="shrink-0"
                                />
                                <p className="font-medium text-gray-900 dark:text-gray-100 truncate">
                                  {resolveUserName(member.userName, t)}
                                </p>
                                {member.isPlaceholder && (
                                  <PlaceholderBadge className="ml-2" />
                                )}
                                <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                              </div>
                              {debt && group.simplifyDebts && (
                                <div className="pl-13 flex flex-col items-end gap-1">
                                  <p className="text-2xl font-bold text-green-600">
                                    {formatCurrency(
                                      debt.amount,
                                      debt.currency || group.currency
                                    )}
                                  </p>
                                  <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {t('settlements:suggestions.youOwe')}
                                  </p>
                                </div>
                              )}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  {errors.toUserId && (
                    <p className="text-sm text-red-500 text-center mt-2">
                      {errors.toUserId}
                    </p>
                  )}
                </div>
              )}

              {/* Step 2: Enter Amount & Method */}
              {currentStep === 2 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('settlements:fields.amount')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('settlements:labels.payingTo')}{' '}
                      <span className="font-semibold text-orange-600 dark:text-orange-400">
                        {resolveUserName(selectedMember?.userName, t)}
                      </span>
                    </p>
                  </div>

                  {/* Amount input */}
                  <div className="flex justify-center">
                    <div className="w-full max-w-md">
                      <CurrencyInput
                        id="amount"
                        name="amount"
                        value={amountString}
                        currency={group.currency}
                        placeholder="0.00"
                        onValueChange={(value, _name, values) => {
                          let numericValue = values?.float ?? 0;

                          // Cap amount to maximum owed if it exceeds the debt
                          const maxOwed = selectedDebt?.amount;
                          if (maxOwed && numericValue > maxOwed) {
                            numericValue = maxOwed;
                            const decimals = getCurrencyDecimals(
                              group.currency
                            );
                            setAmountString(maxOwed.toFixed(decimals));
                          } else {
                            setAmountString(value);
                          }

                          setFormData({
                            ...formData,
                            amount: numericValue,
                          });
                        }}
                        className="text-2xl! font-bold! text-center"
                        autoFocus
                        data-testid="settlement-amount-input"
                      />
                      {errors.amount && (
                        <p className="text-sm text-red-500 text-center mt-2">
                          {errors.amount}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Suggested amount chip */}
                  {selectedDebt && group.simplifyDebts && (
                    <div className="flex justify-center">
                      <button
                        type="button"
                        onClick={handleSuggestedAmount}
                        className="inline-flex items-center gap-2 px-4 py-2.5 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 rounded-lg text-sm font-medium border border-green-200 dark:border-green-800 hover:bg-green-100 dark:hover:bg-green-900/40 hover:border-green-300 dark:hover:border-green-700 transition-all active:scale-[0.98]"
                      >
                        <Check className="h-4 w-4" />
                        <span>{t('settlements:labels.suggestedAmount')}:</span>
                        <span className="font-bold">
                          {formatCurrency(
                            selectedDebt.amount,
                            selectedDebt.currency || group.currency
                          )}
                        </span>
                      </button>
                    </div>
                  )}

                  {/* Payment Method */}
                  <div className="max-w-md mx-auto">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 text-center">
                      {t('settlements:fields.paymentMethod')}
                    </label>
                    <div className="relative">
                      <select
                        id="method"
                        value={formData.method || 'cash'}
                        onChange={e =>
                          setFormData({ ...formData, method: e.target.value })
                        }
                        className="w-full pl-4 pr-10 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 appearance-none text-center font-medium focus:outline-hidden focus:ring-2 focus:ring-orange-500 dark:focus:ring-orange-400 focus:border-transparent"
                        data-testid="settlement-method-select"
                      >
                        {PAYMENT_METHODS.map(method => (
                          <option
                            key={method}
                            value={method}
                            data-testid={`method-option-${method}`}
                          >
                            {t(`settlements:paymentMethods.${method}`)}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-500 pointer-events-none" />
                    </div>
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex justify-between gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      className="flex items-center gap-2"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t('common:actions.back')}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleNext}
                      className="flex items-center gap-2 min-w-[120px]"
                      disabled={!formData.amount || formData.amount <= 0}
                    >
                      {t('common:actions.next')}
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}

              {/* Step 3: Confirm & Notes */}
              {currentStep === 3 && (
                <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
                      {t('settlements:labels.confirmPayment')}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {t('settlements:labels.reviewDetails')}
                    </p>
                  </div>

                  {/* Payment Summary */}
                  <div className="bg-linear-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-xl p-4 sm:p-6 border border-green-200 dark:border-green-800">
                    <div className="space-y-4">
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300 shrink-0">
                          {t('settlements:labels.payingTo')}
                        </span>
                        <span className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                          {resolveUserName(selectedMember?.userName, t)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300 shrink-0">
                          {t('settlements:fields.amount')}
                        </span>
                        <span className="text-2xl font-bold text-green-600 dark:text-green-400 text-right leading-tight">
                          {formatCurrency(formData.amount || 0, group.currency)}
                        </span>
                      </div>
                      <div className="flex justify-between items-center gap-2">
                        <span className="text-sm text-gray-700 dark:text-gray-300 shrink-0">
                          {t('settlements:fields.paymentMethod')}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100 text-right">
                          {t(
                            `settlements:paymentMethods.${formData.method || 'cash'}`
                          )}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Notes (Optional) */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      {t('settlements:fields.notes')}{' '}
                      <span className="text-gray-500">
                        ({t('settlements:fields.optional')})
                      </span>
                    </label>
                    <textarea
                      id="notes"
                      placeholder={t(
                        'settlements:placeholders.additionalNotes'
                      )}
                      value={formData.notes || ''}
                      onChange={e =>
                        setFormData({ ...formData, notes: e.target.value })
                      }
                      rows={3}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-gray-100 bg-white dark:bg-gray-800 resize-none"
                      data-testid="settlement-notes-input"
                    />
                  </div>

                  {/* Navigation buttons */}
                  <div className="flex justify-between gap-3 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={handleBack}
                      disabled={createSettlementMutation.isPending}
                      className="flex items-center gap-2"
                    >
                      <ChevronLeft className="h-4 w-4" />
                      {t('common:actions.back')}
                    </Button>
                    <Button
                      type="button"
                      onClick={handleSubmit}
                      disabled={createSettlementMutation.isPending}
                      className="flex items-center gap-2 min-w-[140px] bg-green-600 hover:bg-green-700"
                      data-testid="create-settlement-submit"
                    >
                      {createSettlementMutation.isPending ? (
                        <>
                          <LoadingSpinner size="sm" />
                          {t('common:creating')}
                        </>
                      ) : (
                        <>
                          <Check className="h-4 w-4" />
                          {t('settlements:actions.settle')}
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </Card>
      </div>
    </Portal>
  );
}
